import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useQuery } from '@apollo/react-hooks';
import { Row, Col, Card, Button, message, Image } from 'antd';
import { LoadingOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import _ from 'lodash';
import { Helmet } from 'react-helmet';
import { InlineWidget } from 'react-calendly';
import { ViewPlanItem } from '../FloorPlans/View';

import { queries } from '../../graphql/graphql';
import _s from './index.less';

import Carousel from '../Carousel/View';
import Recommendation from '../FloorPlans/Recommendation';
import { gaEvent, formatPrice } from '../../utils';
import MapView from '../FloorPlans/MapDataView';
import Summary from '../FloorPlans/Summary';
import ButtonWithConfirm from '../UI/ButtonWithConfirm';

const PhotoGallery = ({ photos, count = 3 }) => {
  if (!photos.length) return null;

  const ListItem = ({ item }) => (
    <Card
      hoverable={true}
      bordered={true}
      bodyStyle={{ padding: 0, margin: 0 }}
      cover={<Image src={item.original} className={_s.galleryImage} />}
    />
  );

  return <Carousel count={count} ListItem={ListItem} items={photos} title="Gallery" />;
};

export const ViewContractor = ({
  contractor,
  plan = {},
  mapId = '',
  PlanComponent,
  consumer = {},
  user
}) => {
  const pricesRef = useRef(null);
  const calendlyRef = useRef(null);
  const [viewCount] = useState(3);
  const [plans, setPlans] = useState([]);
  const [planPhotos, setPlanPhotos] = useState([]);
  const [price, setPrice] = useState({});

  useEffect(() => {
    if (consumer.plans && consumer.plans.length && !plans.length) {
      setPlans(_.filter(consumer.plans, p => p._id !== plan.id));
    }
  }, [consumer]);

  useEffect(() => {
    const photos = [].concat(plan.photos || []);
    setPlanPhotos(photos.filter(photo => photo.isPublish));
  }, [viewCount, plan]);

  const btnStyle = {
    textAlign: 'center',
    width: '300px',
    height: '60px',
    top: '30px',
    border: '1px solid #EE8865',
    boxSizing: 'border-box',
    color: '#000000'
  };

  const textStyleButton = {
    fontStyle: 'normal',
    fontWeight: 230,
    fontSize: '20px',
    lineHeight: '21px'
  };

  // const buyPlan = () => {
  //   gaEvent('planView', 'click:buy_plan', 'Buy Plan');
  //   if (!user) message.error(`Please log in.`);

  //   const hide = message.loading('Action in progress..', 10);

  //   const data = {
  //     user: {
  //       email: user.email,
  //       name: user.name
  //     },
  //     plan: {
  //       id: plan.id,
  //       mapId,
  //       image: plan.image,
  //       title: `${getTitle()} ${getAreaTitle()}`,
  //       contractorId: plan.contractorId
  //     }
  //   };

  //   axios({
  //     method: 'post',
  //     url: '/api/buy-plan',
  //     data
  //   }).then(function(response) {
  //     if (response.status === 200) {
  //       hide();
  //       message.success(`Thank you ${user.name}, you will be contacted shortly.`, 10);
  //     } else {
  //       console.log(response);
  //     }
  //   });
  // };

  const orderSitePlan = () => {
    gaEvent('planView', 'click:order_site_plan', 'Order site plan');
    if (!user) message.error(`Please log in.`);
    const hide = message.loading('Action in progress..', 10);
    const data = {
      user: {
        email: user.email,
        name: user.name
      },
      plan: {
        id: plan.id,
        mapId,
        image: plan.image,
        title: `${getTitle()} ${getAreaTitle()}`,
        contractorId: plan.contractorId
      }
    };

    axios({
      method: 'post',
      url: '/api/order-site-plan',
      data
    }).then(function(response) {
      if (response.status === 200) {
        hide();
        message.success(
          `Thank you, ${user.name}! We will follow up with more details shortly.`,
          10
        );
      } else {
        console.log(response);
      }
    });
  };

  const getTitle = () => {
    if (!meta) return 'House';

    let title = '';
    const { bathroomCount, bedroomCount } = meta;

    if (bedroomCount === 0) title += 'Studio ';
    if (bedroomCount > 0) title += `${bedroomCount} BD`;

    if (bathroomCount) title += ` / ${bathroomCount} BATH`;

    return title;
  };

  const getAreaTitle = () => {
    if (!meta || !meta.totalArea) return '';

    return `${meta.outerArea || meta.totalArea} SQ FT`;
  };

  const { meta, hasARModel, isModular, isManufactured, preApproved, fixPrice } = plan;

  const title = getTitle();
  const areaTitle = getAreaTitle();
  const metaTitle = (title + ' ' + areaTitle).toLowerCase();

  return (
    <div style={{ marginBottom: '70px' }}>
      <Helmet>
        ‍<title>{metaTitle}</title>‍
        <meta name="description" content={metaTitle} />‍
      </Helmet>
      <Row justify="space-around" align="stretch" style={{ backgroundColor: '#F5F5F5' }}>
        <Col span={24} lg={16}>
          {plan && plan.url ? <PlanComponent {...{ ...plan }} /> : <Card>Floor plans data...</Card>}
        </Col>
        <Col
          span={24}
          lg={8}
          style={{ padding: '2.5em', flexDirection: 'column', display: 'flex' }}
        >
          <Row style={{ display: 'flex', flex: 1 }}>
            <Col>
              <span className={_s.titlePlan}>
                {title || 'House'} {areaTitle}
              </span>
              <br />
              {(isModular || isManufactured) && fixPrice && (
                <span className={_s.titlePlan}>Starting from ${formatPrice(fixPrice)}</span>
              )}
              {(!isModular || !isManufactured) && price && price.min && price.max && (
                <span className={_s.titlePlan}>
                  ${formatPrice(price.min)} - ${formatPrice(price.max)}
                  <Button
                    type="text"
                    shape="circle"
                    icon={<QuestionCircleOutlined />}
                    onClick={e => pricesRef.current.scrollIntoView({ behavior: 'smooth' })}
                  />
                </span>
              )}
              {(!isModular || !isManufactured) && price && price.min && price.max && (
                <span className={_s.priceDisclaimer}>
                  Disclaimer: All calculations are estimates and provided for informational purposes
                  only. Actual amounts will vary depending on location, site slope, soil, local
                  labor and utility connection costs.
                </span>
              )}
              {preApproved && (
                <span className={_s.titleOption} style={{ marginTop: 10 }}>
                  <span className={_s.dot} />
                  Pre-Approved
                </span>
              )}
              {isModular && (
                <span className={_s.titleOption}>
                  <span className={_s.dot} />
                  Modular
                </span>
              )}
              {isManufactured && (
                <span className={_s.titleOption}>
                  <span className={_s.dot} />
                  Manufactured
                </span>
              )}
            </Col>
          </Row>
          {hasARModel && (
            <Row gutter={[16, 16]} justify="space-between" align="middle">
              <Col flex="1">
                <Button
                  size="large"
                  style={{ ...btnStyle, ...textStyleButton, width: '100%', top: 0 }}
                  onClick={e =>
                    window.open('https://apps.apple.com/us/app/adu-viewer/id1632652791', '_blank')
                  }
                >
                  View in augmented reality
                </Button>
              </Col>
            </Row>
          )}
          <Row style={{ display: 'flex', marginBottom: 16 }}>
            <Col style={{ width: '100%' }}>
              <ButtonWithConfirm
                title="You are sending the request to get plan and permits."
                styles={{
                  ...btnStyle,
                  ...textStyleButton,
                  width: '100%',
                  top: 0,
                  background: '#EE8865'
                }}
                onConfirm={orderSitePlan}
                text="Get Plan and Permit"
              />
              <Button
                style={{ ...btnStyle, ...textStyleButton, width: '100%', top: 20 }}
                onClick={e => calendlyRef.current.scrollIntoView({ behavior: 'smooth' })}
              >
                Book a Call with ADU Expert
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
      <MapView mapId={mapId} plan={plan} />
      <PhotoGallery photos={planPhotos} count={(planPhotos || []).length} />
      {!isModular && !isManufactured && (
        <div ref={pricesRef}>
          <Summary plan={plan} setPrice={setPrice} />
        </div>
      )}
      <span
        className={_s.block_title}
        style={{ maxWidth: '800px', margin: 'auto', fontSize: '20px', paddingBottom: 0 }}
      >
        Our library carries 512 more plans that might work. Our &quot;Sorting Hat&quot; is
        intelligent but sometimes a discussion between two humans will result in a better solution.
        Let us talk.
      </span>
      <div ref={calendlyRef} style={{ marginTop: '1em' }}>
        <InlineWidget
          styles={{ height: '950px' }}
          url="https://calendly.com/homeworkinprogress/30min"
        />
      </div>
      <Row>
        <Col span={24} lg={8}>
          <Card
            style={{
              height: 250,
              justifyContent: 'center',
              alignItems: 'center',
              display: 'flex',
              margin: '1em'
            }}
          >
            <img src="/public/images/favicon.ico" className={_s.logo} alt="HomeWiP Inc." />
          </Card>
        </Col>
        <Col span={24} lg={16}>
          <span
            className={_s.block_title}
            style={{ justifyContent: 'left', paddingLeft: '15px', paddingBottom: 0 }}
          >
            About
          </span>
          <div className={_s.contractor_about}>{contractor.about}</div>
        </Col>
      </Row>
      <Recommendation plan={plan} />
    </div>
  );
};

const ContractorView = props => {
  const { match } = props;
  const { params } = match;
  const { loading, data = {}, refetch } = useQuery(queries.CONTRACTOR, {
    variables: { id: params.id }
  });
  const { contractor } = data;

  useEffect(() => {
    refetch();
  }, [data]);

  if (!loading && !contractor) return <div>Contractor not found.</div>;

  return (
    <div
      style={{
        display: 'block',
        width: '100vw',
        minHeight: '100vh',
        padding: 0,
        background: '#ffffff'
      }}
    >
      {loading ? (
        <LoadingOutlined
          style={{ justifyContent: 'center', alignItems: 'center', display: 'flex' }}
        />
      ) : (
        <ViewContractor {...{ ...props, contractor, PlanComponent: ViewPlanItem }} />
      )}
    </div>
  );
};

const mapStateToProps = state => {
  return {
    user: state.auth.user,
    email: state.consumer.email
  };
};

const mapDispatchToProps = dispatch => {
  return bindActionCreators({}, dispatch);
};

ContractorView.propTypes = {
  user: PropTypes.object.isRequired,
  email: PropTypes.string
};

const connectedView = connect(mapStateToProps, mapDispatchToProps)(ContractorView);

export default connectedView;
